import React, { useState, useEffect } from 'react';
import '../../styles/AdminPages.css';
import './GestionClubs.css';
import DashboardHeader from '../../components/DashboardHeader';
import AdminNavigationBar from '../../components/AdminNavigationBar';
import clubService from '../../services/clubService';

function GestionClubs() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    objectifs: '',
    image: null,
    responsable: '',
    contact: '',
    activites: [''],
    horaires: [''],
    nombre_membres: 0,
    actif: true
  });

  useEffect(() => {
    loadClubs();
  }, []);

  const loadClubs = async () => {
    try {
      setLoading(true);
      const response = await clubService.admin.getAll();
      setClubs(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      
      // Ajouter tous les champs
      Object.keys(formData).forEach(key => {
        if (key === 'image' && formData[key]) {
          formDataToSend.append(key, formData[key]);
        } else if (key === 'activites') {
          const activites = formData.activites.filter(a => a.trim() !== '');
          formDataToSend.append(key, JSON.stringify(activites));
        } else if (key === 'horaires') {
          const horaires = formData.horaires.filter(h => h.trim() !== '');
          formDataToSend.append(key, JSON.stringify(horaires));
        } else if (key === 'nombre_membres') {
          formDataToSend.append(key, parseInt(formData.nombre_membres) || 0);
        } else if (key !== 'image') {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (editingId) {
        await clubService.admin.update(editingId, formDataToSend);
        alert('Club mis à jour avec succès');
      } else {
        await clubService.admin.create(formDataToSend);
        alert('Club créé avec succès');
      }
      
      setShowModal(false);
      resetForm();
      loadClubs();
    } catch (error) {
      console.error('Erreur complète:', error);
      console.error('Réponse:', error.response?.data);
      alert(`Erreur lors de l'enregistrement: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await clubService.admin.getById(id);
      const club = response.data;
      setFormData({
        nom: club.nom,
        description: club.description,
        objectifs: club.objectifs || '',
        image: null,
        responsable: club.responsable || '',
        contact: club.contact || '',
        activites: club.activites || [''],
        horaires: club.horaires || [''],
        nombre_membres: club.nombre_membres,
        actif: club.actif
      });
      setEditingId(id);
      setShowModal(true);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce club ?')) return;
    try {
      await clubService.admin.delete(id);
      alert('Club supprimé avec succès');
      loadClubs();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      description: '',
      objectifs: '',
      image: null,
      responsable: '',
      contact: '',
      activites: [''],
      horaires: [''],
      nombre_membres: 0,
      actif: true
    });
    setEditingId(null);
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Gestion des Clubs" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          <div className="page-header">
            <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
              + Nouveau Club
            </button>
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <div className="clubs-grid">
              {clubs.map((club) => (
                <div key={club.id} className="club-card">
                  {club.image && (
                    <div className="club-image">
                      <img src={club.image} alt={club.nom} />
                    </div>
                  )}
                  <div className="club-content">
                    <div className="club-header">
                      <h3>{club.nom}</h3>
                      <span className={`badge ${club.actif ? 'badge-success' : 'badge-draft'}`}>
                        {club.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <p>{club.description}</p>
                    <div className="club-meta">
                      <span>👥 {club.nombre_membres} membres</span>
                      {club.responsable && <span>👨‍🏫 {club.responsable}</span>}
                    </div>
                    <div className="action-buttons">
                      <button className="btn-edit" onClick={() => handleEdit(club.id)}>Modifier</button>
                      <button className="btn-delete" onClick={() => handleDelete(club.id)}>Supprimer</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showModal && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editingId ? 'Modifier le club' : 'Nouveau club'}</h2>
                  <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
                </div>
                <form onSubmit={handleSubmit} className="club-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nom *</label>
                      <input type="text" value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label>Nombre de membres</label>
                      <input type="number" min="0" value={formData.nombre_membres} onChange={(e) => setFormData({ ...formData, nombre_membres: e.target.value })} />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description *</label>
                    <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" required />
                  </div>

                  <div className="form-group">
                    <label>Objectifs</label>
                    <textarea value={formData.objectifs} onChange={(e) => setFormData({ ...formData, objectifs: e.target.value })} rows="3" />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Responsable</label>
                      <input type="text" value={formData.responsable} onChange={(e) => setFormData({ ...formData, responsable: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Contact</label>
                      <input type="text" value={formData.contact} onChange={(e) => setFormData({ ...formData, contact: e.target.value })} />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Image</label>
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })} 
                    />
                  </div>

                  <div className="form-group">
                    <label>Activités</label>
                    {formData.activites.map((activite, index) => (
                      <div key={index} className="array-field">
                        <input type="text" value={activite} onChange={(e) => handleArrayFieldChange('activites', index, e.target.value)} placeholder="Activité..." />
                        {formData.activites.length > 1 && <button type="button" onClick={() => removeArrayField('activites', index)}>×</button>}
                      </div>
                    ))}
                    <button type="button" className="btn-add-field" onClick={() => addArrayField('activites')}>+ Ajouter une activité</button>
                  </div>

                  <div className="form-group">
                    <label>Horaires</label>
                    {formData.horaires.map((horaire, index) => (
                      <div key={index} className="array-field">
                        <input type="text" value={horaire} onChange={(e) => handleArrayFieldChange('horaires', index, e.target.value)} placeholder="Ex: Mercredi 14h-16h" />
                        {formData.horaires.length > 1 && <button type="button" onClick={() => removeArrayField('horaires', index)}>×</button>}
                      </div>
                    ))}
                    <button type="button" className="btn-add-field" onClick={() => addArrayField('horaires')}>+ Ajouter un horaire</button>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" checked={formData.actif} onChange={(e) => setFormData({ ...formData, actif: e.target.checked })} />
                      Club actif
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Annuler</button>
                    <button type="submit" className="btn-submit">{editingId ? 'Mettre à jour' : 'Créer'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default GestionClubs;